/* eslint-disable prettier/prettier */

/* eslint-disable @typescript-eslint/no-empty-function */
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardNumberElement } from '@stripe/stripe-js';
import createPaymentMethodApi from 'api/paymentMethod';
import createUserApi from 'api/user';
import { setPaymentMethod, setPaymentMethods } from 'modules/payments/paymentsSlice';
import { currentUserSelector, setAvailablePaymentMethods, updateDefaultPaymentMethod } from 'modules/user/userSlice';
import { createContext, FC, useCallback, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useTranslation } from 'react-i18next';
import { appContextSelector, isCorporateThemeSelector } from 'modules/app/appSlice';
interface State {
  loading: boolean;
  error?: string;
  currency: string;
}

interface ContextState extends State {
  createOrUpdatePaymentMethod: (name: string, onSuccess?: () => void) => Promise<void>;
  deletePaymentMethod: () => Promise<void>;
}

const defaultState: State = {
  loading: false,
  currency: 'USD',
};

const contextDefaultValue: ContextState = {
  ...defaultState,
  createOrUpdatePaymentMethod: async () => {},
  deletePaymentMethod: async () => {},
};

const PaymentMethodContext = createContext<ContextState>(contextDefaultValue);

const PaymentMethodContextProvider: FC = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const stripe = useStripe();
  const elements = useElements();
  const currentAccount = useSelector(currentUserSelector);
  const { paymentMethod, paymentMethods, paymentMethodTypes } = useSelector((state: RootState) => state.payments);
  const context = useSelector(appContextSelector);
  const isWeChooose = context === 'wechooose';
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const dispatch = useDispatch();
  const { t } = useTranslation('paymentMethod');
  const TIMEOUT_IN_SECONDS = 30;
  const isStripeDefaultPaymentMethod =
    paymentMethod?.type === 'Stripe' || (!paymentMethod && (paymentMethodTypes === null || paymentMethodTypes?.includes('Stripe')));
  const stripePaymentMethod = paymentMethods.find(({ type }) => type.toLowerCase() === 'stripe');

  const waitForPaymentMethodStatusUpdate = async (customerId: string): Promise<void> => {
    const userApi = createUserApi(context);
    for (let i = 0; i < TIMEOUT_IN_SECONDS; i++) {
      const customer = await userApi.getCustomer(customerId);
      if (!customer) {
        throw new Error(t('cardSetupError'));
      }

      const cardDetails =
        isCorporateTheme || isWeChooose
          ? customer.payment?.paymentMethods.find(({ type }) => type === 'Stripe')
          : customer.payment?.defaultPaymentMethod;

      if (cardDetails?.status?.toLowerCase() === 'valid') {
        dispatch(setAvailablePaymentMethods(customer.payment?.paymentMethods ?? []));
        dispatch(setPaymentMethods(customer.payment?.paymentMethods ?? []));

        if (!isCorporateTheme || (customer.payment?.defaultPaymentMethod?.type === 'Stripe' && isStripeDefaultPaymentMethod)) {
          dispatch(setPaymentMethod(cardDetails));
          dispatch(updateDefaultPaymentMethod(cardDetails));
        }
        return;
      }

      dispatch(setPaymentMethods(customer.payment?.paymentMethods ?? []));

      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    throw new Error(t('cardSetupError'));
  };

  const createOrUpdatePaymentMethod = async (name: string, onSuccess?: () => void): Promise<void> => {
    if (!stripe || !elements) {
      return;
    }
    setLoading(true);
    setError(undefined);

    try {
      const paymentMethodApi = createPaymentMethodApi(context);
      let currentPaymentMethod = paymentMethod;
      let newPaymentMethod;
      if (!isCorporateTheme && (!currentPaymentMethod || currentPaymentMethod.type.toLowerCase() !== 'stripe' || currentPaymentMethod.deleted)) {
        currentPaymentMethod = await paymentMethodApi.createStripePaymentMethod(currentAccount?.customerId ?? '');
      }

      if ((isCorporateTheme || isWeChooose) && (!stripePaymentMethod || stripePaymentMethod.deleted)) {
        newPaymentMethod = await paymentMethodApi.createStripePaymentMethod(currentAccount?.customerId ?? '');
      }

      const intent =
        isCorporateTheme || isWeChooose
          ? (newPaymentMethod || stripePaymentMethod) &&
            (await paymentMethodApi.setupCard(
              currentAccount?.customerId ?? '',
              newPaymentMethod?.id ?? stripePaymentMethod?.id ?? '',
              newPaymentMethod?.weight ?? stripePaymentMethod?.weight ?? 1,
            ))
          : currentPaymentMethod && (await paymentMethodApi.setupCard(currentAccount?.customerId ?? '', currentPaymentMethod.id, 1));

      const result =
        intent &&
        (await stripe.confirmCardSetup(intent.clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement) as StripeCardNumberElement,
            billing_details: {
              name,
            },
          },
        }));

      if (result?.error) {
        const paymentMethodIncomplete = { ...(currentPaymentMethod as PaymentMethod), type: 'Stripe', status: 'Incomplete' };
        //@ts-ignore
        (isStripeDefaultPaymentMethod || !isCorporateTheme) && dispatch(setPaymentMethod(paymentMethodIncomplete));
        isCorporateTheme || (isWeChooose && dispatch(setPaymentMethods([...paymentMethods, paymentMethodIncomplete])));
        setError(result.error.message);
        setLoading(false);
        return;
      }

      await waitForPaymentMethodStatusUpdate(currentAccount?.customerId ?? '');

      setLoading(false);
      onSuccess?.();
    } catch (err) {
      const paymentMethodBeingUpdated = isStripeDefaultPaymentMethod ? paymentMethod : stripePaymentMethod;
      const paymentMethodIncomplete = { ...(paymentMethodBeingUpdated as PaymentMethod), type: 'Stripe', status: 'Incomplete' };
      //@ts-ignore
      (isStripeDefaultPaymentMethod || !isCorporateTheme) && dispatch(setPaymentMethod(paymentMethodIncomplete));
      (isCorporateTheme || isWeChooose) && dispatch(setPaymentMethods([...paymentMethods, paymentMethodIncomplete]));
      setError(err?.message ?? t('error'));
      setLoading(false);
    }
  };

  const deletePaymentMethod = useCallback(async () => {
    if (!paymentMethod || paymentMethod.deleted) {
      return;
    }

    try {
      const paymentMethodApi = createPaymentMethodApi(context);
      setLoading(true);
      await paymentMethodApi.deletePaymentMethod(currentAccount?.customerId ?? '', paymentMethod.id);
      setLoading(false);
    } catch (err) {
      setError(err?.message ?? t('error'));
      setLoading(false);
    }
  }, [setLoading, setError, currentAccount, context]);

  const currency = currentAccount?.customer?.payment?.currency ?? 'USD';
  return (
    <PaymentMethodContext.Provider value={{ loading, error, currency, createOrUpdatePaymentMethod, deletePaymentMethod }}>
      {children}
    </PaymentMethodContext.Provider>
  );
};

const usePaymentMethod = (): ContextState => {
  const context = useContext(PaymentMethodContext);
  if (context === undefined) {
    throw new Error('usePaymentMethod must be used within a PaymentMethodContextProvider');
  }

  return context;
};

export { PaymentMethodContextProvider, usePaymentMethod };
